import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Loader2, CheckCircle, Copy, Trash2, Percent, Eye, MousePointerClick,
  Send, BellRing, Rocket, Edit3, RefreshCw, Upload
} from 'lucide-react';

export const OffersSection = ({
  myOffers, offersLoading, showOfferForm, setShowOfferForm,
  offerSubmitting, offerForm, setOfferForm,
  offerErrors, setOfferErrors,
  editingOfferId, setEditingOfferId,
  handleOfferSubmit, handleDeleteOffer, openEditOffer, handleOfferUpdate,
  handleDuplicateOffer, restoreOffer,
  handleOfferImageUpload, offerImageUploading, offerImageInputRef,
  boostTiers, boostModal, setBoostModal,
  selectedTier, setSelectedTier, boostLoading,
  handleBoostCheckout,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState('active');
  const [restoreState, setRestoreState] = React.useState({ busy: null, msg: null });

  const onRestore = async (id) => {
    if (!restoreOffer) return;
    setRestoreState({ busy: id, msg: null });
    const r = await restoreOffer(id);
    setRestoreState({ busy: null, msg: r.ok ? { kind: 'ok', text: t('restored', 'Restored') } : { kind: 'err', text: r.error || t('restoreFailed', 'Could not restore. Please try again.') } });
    if (r.ok) setTab('active');
    setTimeout(() => setRestoreState({ busy: null, msg: null }), 4000);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="offers-section">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Percent className="h-5 w-5 text-orange-500" />
              {t('offers')}
            </h2>
            {!showOfferForm && (
              <button onClick={() => { setEditingOfferId?.(null); setShowOfferForm(true); }}
                className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1"
                data-testid="create-offer-btn">
                <Percent className="h-3.5 w-3.5" />{t('createOffer')}
              </button>
            )}
          </div>
        </div>

        {/* Create Offer Form */}
        {showOfferForm && (
          <div className="p-4 border-b bg-orange-50/50" data-testid="create-offer-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{editingOfferId ? t('editOffer', 'Edit Offer') : t('newOffer')}</h3>
              <button onClick={() => { setShowOfferForm(false); setOfferErrors({}); setEditingOfferId?.(null); setOfferForm(f => ({ ...f, duplicatedFrom: null })); }} className="p-1.5 hover:bg-gray-200 rounded-lg">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            {!editingOfferId && offerForm.duplicatedFrom && (
              <div className="mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-800 flex items-center gap-2" data-testid="offer-duplicate-banner">
                <Copy className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{t('recreateOfferBanner', { defaultValue: 'Recreating a new offer from "{{title}}". Set a new end date to publish.', title: offerForm.duplicatedFrom })}</span>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('offerTitle')} *</label>
                <input type="text" value={offerForm.title} onChange={e => setOfferForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={t('offerTitlePlaceholder')}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none ${offerErrors.title ? 'border-red-400' : 'border-gray-300'}`}
                  data-testid="offer-title-input" />
                {offerErrors.title && <p className="text-xs text-red-600 mt-1">{offerErrors.title}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('offerDescription')} *</label>
                <textarea value={offerForm.description} onChange={e => setOfferForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('offerDescPlaceholder')} rows={2}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none resize-none ${offerErrors.description ? 'border-red-400' : 'border-gray-300'}`}
                  data-testid="offer-description-input" />
                {offerErrors.description && <p className="text-xs text-red-600 mt-1">{offerErrors.description}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('discountPercent')}</label>
                  <div className="relative">
                    <input type="number" min="0" max="100" step="1" value={offerForm.discountPercentage}
                      onChange={e => setOfferForm(f => ({ ...f, discountPercentage: e.target.value }))}
                      placeholder="e.g. 20"
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                      data-testid="offer-discount-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('validUntil')} *</label>
                  <input type="date" value={offerForm.validUntil} onChange={e => setOfferForm(f => ({ ...f, validUntil: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    max={editingOfferId && offerForm.originalValidUntil ? new Date(offerForm.originalValidUntil).toISOString().split('T')[0] : undefined}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none ${offerErrors.validUntil ? 'border-red-400' : 'border-gray-300'}`}
                    data-testid="offer-valid-until-input" />
                  {offerErrors.validUntil && <p className="text-xs text-red-600 mt-1">{offerErrors.validUntil}</p>}
                  {editingOfferId && offerForm.originalValidUntil && (
                    <p className="text-xs text-gray-500 mt-1" data-testid="offer-edit-date-hint">
                      {t('editDateHintOffer', 'You can keep or move this date earlier — but it cannot be extended past the original end date. Create a new offer instead.')}
                    </p>
                  )}
                </div>
              </div>
              {/* Iter 184 — single offer image, parity with the event poster
                  block above. Paid multi-photo galleries land in a follow-up. */}
              {handleOfferImageUpload && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('offerImage', 'Offer image')}</label>
                  <input
                    ref={offerImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleOfferImageUpload}
                    className="hidden"
                    data-testid="offer-image-input"
                  />
                  {offerForm.image ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={offerForm.image}
                        alt="Offer"
                        className="h-16 w-16 object-cover rounded-lg border"
                        data-testid="offer-image-preview"
                      />
                      <button
                        type="button"
                        onClick={() => setOfferForm(f => ({ ...f, image: null }))}
                        className="text-sm text-red-600 hover:text-red-800"
                        data-testid="offer-image-remove-btn"
                      >
                        {t('removeImage', 'Remove image')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => offerImageInputRef?.current?.click()}
                      disabled={offerImageUploading}
                      className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-orange-400 hover:text-orange-600 transition-colors w-full flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="offer-image-upload-btn"
                    >
                      {offerImageUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {offerImageUploading ? t('uploading', 'Uploading…') : t('uploadOfferImage', 'Upload image')}
                    </button>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2 pt-1">
                {!editingOfferId && (
                  <>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="offer-notify-toggle">
                      <div className={`relative w-10 h-5 rounded-full transition-colors ${offerForm.notifyFollowers ? 'bg-orange-500' : 'bg-gray-300'}`}
                        onClick={() => setOfferForm(f => ({ ...f, notifyFollowers: !f.notifyFollowers }))}>
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${offerForm.notifyFollowers ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <BellRing className="h-4 w-4 text-orange-500" />
                        <span className="text-sm text-gray-700">{t('notifyFollowers')}</span>
                      </div>
                    </label>
                    <div className="flex items-center gap-3 opacity-50 cursor-not-allowed" data-testid="offer-boost-placeholder">
                      <div className="relative w-10 h-5 rounded-full bg-gray-200">
                        <div className="absolute top-0.5 translate-x-0.5 w-4 h-4 bg-white rounded-full shadow" />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Rocket className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-400">{t('boostPost')}</span>
                        <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-medium">{t('boostAfterCreate')}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button onClick={editingOfferId ? handleOfferUpdate : handleOfferSubmit} disabled={offerSubmitting}
                className="w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-offer-btn">
                {offerSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {offerSubmitting ? (editingOfferId ? t('saving', 'Saving...') : t('creatingOffer')) : (editingOfferId ? t('saveChanges', 'Save Changes') : t('publishOffer'))}
              </button>
            </div>
          </div>
        )}

        {/* Offers list */}
        <div className="p-4">
          {offersLoading ? (
            <div className="text-center py-6 text-gray-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />{t('loadingOffers')}</div>
          ) : myOffers.length === 0 ? (
            <div className="text-center py-6 text-gray-400"><Percent className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>{t('noOffersCreated')}</p></div>
          ) : (() => {
            const now = new Date();
            const isArchivedRow = (o) => !!o.archivedAt || new Date(o.validUntil) < now;
            const activeOffers = myOffers.filter(o => !isArchivedRow(o));
            const archivedOffers = myOffers.filter(isArchivedRow);
            const visible = tab === 'archived' ? archivedOffers : activeOffers;
            const renderRow = (offer, isExpired) => (
              <div
                key={offer.id}
                className={`border rounded-lg p-3 ${isExpired ? 'bg-gray-50 border-gray-200 opacity-80' : 'bg-orange-50 border-orange-200'}`}
                data-testid={`my-offer-${offer.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className={`font-medium truncate ${isExpired ? 'text-gray-600' : 'text-gray-900'}`}>{offer.title}</h4>
                      {isExpired && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gray-200 text-gray-700"
                          data-testid={`offer-expired-badge-${offer.id}`}
                        >
                          {offer.archivedAt && new Date(offer.validUntil) >= now ? t('tabArchived', 'Archived') : t('expired', 'Expired')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{offer.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {offer.discountPercentage && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isExpired ? 'bg-gray-200 text-gray-600' : 'bg-orange-100 text-orange-700'}`}>
                          {offer.discountPercentage}% off
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{t('validUntil')}: {new Date(offer.validUntil).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 flex-shrink-0 ml-2">
                    {!isExpired && (
                      <button onClick={() => openEditOffer(offer)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
                        data-testid={`edit-offer-btn-${offer.id}`}>
                        <Edit3 className="h-3 w-3" />{t('edit', 'Edit')}
                      </button>
                    )}
                    {handleDuplicateOffer && (
                      <button onClick={() => handleDuplicateOffer(offer)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        data-testid={`duplicate-offer-btn-${offer.id}`}
                        title={t('recreateOfferTooltip', 'Create a new offer from this one — set a new end date to publish')}>
                        <Copy className="h-3 w-3" />{t('recreateOffer', 'Recreate')}
                      </button>
                    )}
                    {/* Restore: only if archived AND validUntil still in the future */}
                    {offer.archivedAt && new Date(offer.validUntil) > now && (
                      <button
                        onClick={() => onRestore(offer.id)}
                        disabled={restoreState.busy === offer.id}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors disabled:opacity-60"
                        data-testid={`restore-offer-btn-${offer.id}`}
                      >
                        <RefreshCw className="h-3 w-3" />{restoreState.busy === offer.id ? '…' : t('restore', 'Restore')}
                      </button>
                    )}
                    {!offer.archivedAt && (
                      <button onClick={() => handleDeleteOffer(offer.id)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                        data-testid={`delete-offer-btn-${offer.id}`}>
                        <Trash2 className="h-3 w-3" />{t('archive', 'Archive')}
                      </button>
                    )}
                  </div>
                </div>
                <div className={`flex items-center gap-3 mt-2 pt-2 border-t text-[11px] text-gray-500 ${isExpired ? 'border-gray-200' : 'border-orange-200/60'}`} data-testid={`offer-analytics-${offer.id}`}>
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{offer.views || 0} {t('analyticsViews')}</span>
                  <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{offer.clicks || 0} {t('analyticsClicks')}</span>
                  {offer.views > 0 && <span className="text-orange-600 font-medium">{Math.round((offer.clicks || 0) / offer.views * 100)}% CTR</span>}
                  {(offer.pushSent > 0 || offer.emailSent > 0) && (
                    <span className="flex items-center gap-1"><Send className="h-3 w-3" />{(offer.pushSent || 0) + (offer.emailSent || 0)} {t('sent')}</span>
                  )}
                  {!isExpired && (
                    <div className="ml-auto">
                      {offer.boosted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full" data-testid={`offer-boosted-badge-${offer.id}`}>
                          <Rocket className="h-2.5 w-2.5" />{t('boosted')}
                        </span>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); setBoostModal({ postId: offer.id, postType: 'offer', title: offer.title }); setSelectedTier('24h'); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-600 text-[10px] font-medium rounded-full hover:bg-orange-200 transition-colors"
                          data-testid={`boost-offer-btn-${offer.id}`}>
                          <Rocket className="h-2.5 w-2.5" />{t('boostPost')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
            return (
              <div className="space-y-3">
                {/* Tabs */}
                <div className="flex gap-2" data-testid="offers-tabs">
                  <button
                    type="button"
                    onClick={() => setTab('active')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'active' ? 'bg-orange-100 text-orange-700' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="offers-tab-active"
                    data-state={tab === 'active' ? 'active' : 'inactive'}
                  >
                    {t('tabActive', 'Active')} ({activeOffers.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('archived')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'archived' ? 'bg-gray-100 text-gray-700' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid="offers-tab-archived"
                    data-state={tab === 'archived' ? 'active' : 'inactive'}
                  >
                    {t('tabArchived', 'Archived')} ({archivedOffers.length})
                  </button>
                </div>
                {restoreState.msg && (
                  <p className={`text-xs ${restoreState.msg.kind === 'ok' ? 'text-green-700' : 'text-red-700'}`} data-testid="offers-restore-toast">
                    {restoreState.msg.text}
                  </p>
                )}
                {visible.length === 0 ? (
                  <div className="text-xs text-gray-400 py-2">
                    {tab === 'archived' ? t('noArchivedItems', 'Nothing archived yet.') : t('noActiveOffers', 'No active offers right now.')}
                  </div>
                ) : (
                  <div className="space-y-3">{visible.map((o) => renderRow(o, tab === 'archived'))}</div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Boost Payment Modal */}
      {boostModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="boost-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><Rocket className="h-5 w-5 text-white" /><h3 className="text-white font-bold">{t('boostPost')}</h3></div>
              <button onClick={() => setBoostModal(null)} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-1">{t('boostModalDesc')}</p>
              <p className="text-sm font-medium text-gray-900 mb-4">"{boostModal.title}"</p>
              <div className="space-y-2 mb-5">
                {boostTiers.map(tier => (
                  <label key={tier.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${selectedTier === tier.id ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`boost-tier-${tier.id}`}>
                    <input type="radio" name="boost-tier" className="sr-only" checked={selectedTier === tier.id} onChange={() => setSelectedTier(tier.id)} />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedTier === tier.id ? 'border-orange-500' : 'border-gray-300'}`}>
                      {selectedTier === tier.id && <div className="w-2 h-2 bg-orange-500 rounded-full" />}
                    </div>
                    <div className="flex-1"><span className="text-sm font-medium text-gray-900">{tier.label}</span></div>
                    <span className="text-sm font-bold text-orange-600">{tier.amount.toFixed(0)}&#8364;</span>
                  </label>
                ))}
              </div>
              <button onClick={handleBoostCheckout} disabled={boostLoading}
                className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold text-sm hover:from-amber-500 hover:to-orange-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="boost-checkout-btn">
                {boostLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                {boostLoading ? t('redirecting') : t('boostPayNow')}
              </button>
              <p className="text-xs text-gray-400 text-center mt-3">{t('boostSecurePayment')}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
